import { useState, useEffect, useCallback } from "react";
import {
  Building2, Users, Search, Play, RotateCcw, ArrowLeft,
  Dumbbell, ChevronRight, X, User, Info
} from "lucide-react";
import AccountPage from "../AccountPage";
import SporterProfielModal from "./SporterProfielModal";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

// ── KIOSK ACCOUNT VIEW (identiek aan GroepModus) ───────────────────────────────
function KioskAccountView({ kioskToken }) {
  const [kioskUser, setKioskUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!kioskToken) return;
    fetch(`${API}/api/auth/me`, {
      headers: { Authorization: `Bearer ${kioskToken}` },
    })
      .then(r => r.json())
      .then(d => { setKioskUser({ ...d, token: kioskToken }); setLoading(false); })
      .catch(() => { setError("Sporter dashboard laden mislukt"); setLoading(false); });
  }, [kioskToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: GOLD, borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  return <AccountPage kioskUser={kioskUser} />;
}

// ── SPORTER TEGEL ──────────────────────────────────────────────────────────────
function SporterTegel({ sporter, onSelect, onProfiel }) {
  const initials = (sporter.naam || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const colors = ["#C9A84C", "#4C9A84", "#844CC9", "#C94C4C", "#4C84C9"];
  const color = colors[(sporter.naam || "").charCodeAt(0) % colors.length];

  return (
    <div className="flex flex-col gap-3 p-5 rounded-2xl border border-white/8 bg-white/3 hover:border-white/15 transition-all group">
      {/* Profiel klikbare zone */}
      <button
        data-testid={`admin-sporter-profiel-${sporter.id}`}
        onClick={() => onProfiel(sporter)}
        className="flex flex-col items-center gap-2 w-full"
      >
        <div className="relative">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-black text-black"
            style={{ background: color }}>
            {initials}
          </div>
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#0d0d0d] border border-white/10 flex items-center justify-center">
            <Info size={10} className="text-zinc-500" />
          </div>
        </div>
        <div className="text-center">
          <p className="text-white font-bold text-sm leading-tight">{sporter.naam}</p>
          <p className="text-zinc-600 text-xs mt-0.5 truncate max-w-[120px]">{sporter.email}</p>
        </div>
      </button>
      {/* Training starten knop */}
      <button
        data-testid={`admin-train-sporter-${sporter.id}`}
        onClick={() => onSelect(sporter)}
        className="flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl transition-all hover:opacity-100 opacity-80 w-full"
        style={{ background: `${GOLD}15`, color: GOLD }}
      >
        <Play size={10} fill="currentColor" /> Training starten
      </button>
    </div>
  );
}

// ── BEDRIJF KAART ──────────────────────────────────────────────────────────────
function BedrijfKaart({ bedrijf, onSelect }) {
  return (
    <button
      data-testid={`admin-train-bedrijf-${bedrijf.id}`}
      onClick={() => onSelect(bedrijf)}
      className="w-full flex items-center justify-between px-5 py-4 rounded-2xl border border-white/8 bg-white/3 hover:bg-white/6 hover:border-[#C9A84C]/30 transition-all active:scale-[0.99] group"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-black text-black shrink-0"
          style={{ background: bedrijf.branding?.primary_color || GOLD }}>
          {(bedrijf.naam || "?")[0].toUpperCase()}
        </div>
        <div className="text-left">
          <p className="text-white font-bold text-sm">{bedrijf.naam}</p>
          <p className="text-zinc-500 text-xs mt-0.5">
            {bedrijf.sporter_count ?? 0} sporters · {bedrijf.schema_count ?? 0} schema's
          </p>
        </div>
      </div>
      <ChevronRight size={16} className="text-zinc-600 group-hover:text-[#C9A84C] transition-colors" />
    </button>
  );
}

// ── EIGEN SPORTERS KAART (Samuel's B2C klanten) ────────────────────────────────
function EigenSportersKaart({ count, onSelect }) {
  return (
    <button
      data-testid="admin-train-eigen-sporters"
      onClick={onSelect}
      className="w-full flex items-center justify-between px-5 py-4 rounded-2xl border border-[#C9A84C]/25 bg-[#C9A84C]/5 hover:bg-[#C9A84C]/10 hover:border-[#C9A84C]/40 transition-all active:scale-[0.99] group"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-black text-black shrink-0"
          style={{ background: GOLD }}>
          <User size={20} className="text-black" />
        </div>
        <div className="text-left">
          <p className="font-bold text-sm" style={{ color: GOLD }}>Mijn Sporters</p>
          <p className="text-zinc-500 text-xs mt-0.5">
            {count} eigen klanten met schema's
          </p>
        </div>
      </div>
      <ChevronRight size={16} className="text-zinc-600 group-hover:text-[#C9A84C] transition-colors" />
    </button>
  );
}

// ── MAIN TRAIN MODUS TAB ────────────────────────────────────────────────────────
export default function TrainModusTab({ token }) {
  const [stap, setStap] = useState("bedrijven"); // "bedrijven" | "sporters" | "actief"
  const [bedrijven, setBedrijven] = useState([]);
  const [bedrijvenLoading, setBedrijvenLoading] = useState(true);
  const [eigenSporters, setEigenSporters] = useState([]);
  const [geselecteerdBedrijf, setGeselecteerdBedrijf] = useState(null);
  const [sporters, setSporters] = useState([]);
  const [sportersLoading, setSportersLoading] = useState(false);
  const [zoekterm, setZoekterm] = useState("");
  const [activeSporter, setActiveSporter] = useState(null);
  const [kioskToken, setKioskToken] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [profielSporter, setProfielSporter] = useState(null); // sporter voor profielmodal

  const authHeaders = { Authorization: `Bearer ${token}` };

  // Laad bedrijven + eigen sporters parallel
  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/companies`, { headers: authHeaders }).then(r => r.json()),
      fetch(`${API}/api/admin/own-sporters`, { headers: authHeaders }).then(r => r.json()),
    ])
      .then(([bedrijvenData, eigenData]) => {
        setBedrijven(bedrijvenData.companies || []);
        setEigenSporters(eigenData.sporters || []);
        setBedrijvenLoading(false);
      })
      .catch(() => {
        setError("Laden mislukt");
        setBedrijvenLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Laad sporters van geselecteerd bedrijf of eigen sporters
  const laadSporters = useCallback(async (bedrijf) => {
    setGeselecteerdBedrijf(bedrijf);
    setSportersLoading(true);
    setStap("sporters");
    setZoekterm("");

    if (bedrijf.id === "__eigen__") {
      setSporters(eigenSporters);
      setSportersLoading(false);
      return;
    }

    try {
      const r = await fetch(`${API}/api/admin/companies/${bedrijf.id}/sporters`, { headers: authHeaders });
      const d = await r.json();
      setSporters(Array.isArray(d) ? d : []);
    } catch {
      setError("Sporters laden mislukt");
    } finally {
      setSportersLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, eigenSporters]);

  // Start kiosk sessie voor gekozen sporter
  const startSessie = useCallback(async (sporter) => {
    setStarting(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/admin/groep/start-session/${sporter.id}`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
      });
      if (!r.ok) throw new Error("Sessie starten mislukt");
      const data = await r.json();
      setKioskToken(data.token);
      setActiveSporter(data.sporter);
      setStap("actief");
    } catch (e) {
      setError(e.message);
    } finally {
      setStarting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const wisselSporter = () => {
    setActiveSporter(null);
    setKioskToken(null);
    setStap("sporters");
  };

  const terugnaarBedrijven = () => {
    setGeselecteerdBedrijf(null);
    setSporters([]);
    setStap("bedrijven");
  };

  // ── ACTIEVE KIOSK SESSIE ────────────────────────────────────────────────────
  if (stap === "actief" && activeSporter && kioskToken) {
    return (
      <div className="relative min-h-screen -mx-4 -mt-8">
        <div className="fixed top-4 right-4 z-[100]">
          <button
            data-testid="admin-wissel-sporter-btn"
            onClick={wisselSporter}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm shadow-xl transition-all active:scale-95 border border-white/15"
            style={{ background: "#0f0f0f" }}>
            <RotateCcw size={14} style={{ color: GOLD }} />
            <span className="text-white">Wissel sporter</span>
          </button>
        </div>
        <div className="fixed top-4 left-4 z-[100]">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 text-xs font-bold"
            style={{ background: "#0f0f0f" }}>
            <div className="w-5 h-5 rounded-lg flex items-center justify-center text-[10px] font-black text-black"
              style={{ background: GOLD }}>
              {(activeSporter.naam || "?")[0].toUpperCase()}
            </div>
            <span className="text-white">{activeSporter.naam}</span>
            <span className="text-zinc-600 font-normal">Train Modus</span>
          </div>
        </div>
        <KioskAccountView kioskToken={kioskToken} />
      </div>
    );
  }

  const gefiltereSporters = sporters.filter(s =>
    !zoekterm ||
    (s.naam || "").toLowerCase().includes(zoekterm.toLowerCase()) ||
    (s.email || "").toLowerCase().includes(zoekterm.toLowerCase())
  );

  const gefilterdeBedrijven = bedrijven.filter(b =>
    !zoekterm || (b.naam || "").toLowerCase().includes(zoekterm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        {stap === "sporters" && (
          <button
            data-testid="admin-train-terug-bedrijven"
            onClick={terugnaarBedrijven}
            className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors shrink-0"
          >
            <ArrowLeft size={16} className="text-zinc-400" />
          </button>
        )}
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}20` }}>
              <Dumbbell size={14} style={{ color: GOLD }} />
            </div>
            <h2 className="text-white font-black text-lg">
              {stap === "bedrijven"
                ? "Train Modus"
                : geselecteerdBedrijf?.id === "__eigen__"
                  ? "Mijn Sporters — Selecteer sporter"
                  : `${geselecteerdBedrijf?.naam} — Selecteer sporter`}
            </h2>
          </div>
          <p className="text-zinc-600 text-xs">
            {stap === "bedrijven"
              ? "Kies een groep en start een training als sporter"
              : `${sporters.length} sporters beschikbaar`}
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
          <X size={14} />
          {error}
          <button onClick={() => setError("")} className="ml-auto text-red-600 hover:text-red-400"><X size={12} /></button>
        </div>
      )}

      {/* Zoekbalk */}
      <div className="relative">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input
          data-testid="admin-train-zoekbalk"
          type="text"
          placeholder={stap === "bedrijven" ? "Zoek bedrijf of sporter..." : "Zoek sporter op naam of e-mail..."}
          value={zoekterm}
          onChange={e => setZoekterm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-white/8 bg-[#111] text-white text-sm placeholder:text-zinc-700 focus:outline-none focus:border-white/20 transition-colors"
        />
      </div>

      {/* ── STAP 1: BEDRIJVEN + EIGEN SPORTERS ── */}
      {stap === "bedrijven" && (
        <>
          {bedrijvenLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-20 rounded-2xl bg-white/3 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-3" data-testid="admin-train-bedrijven-lijst">
              {/* Eigen sporters bovenaan als ze er zijn */}
              {eigenSporters.length > 0 && !zoekterm && (
                <EigenSportersKaart
                  count={eigenSporters.length}
                  onSelect={() => laadSporters({ id: "__eigen__", naam: "Mijn Sporters" })}
                />
              )}
              {gefilterdeBedrijven.length === 0 && eigenSporters.length === 0 ? (
                <div className="text-center py-16">
                  <Building2 size={40} className="text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-400 font-semibold">Geen bedrijven gevonden</p>
                </div>
              ) : (
                gefilterdeBedrijven.map(b => (
                  <BedrijfKaart key={b.id} bedrijf={b} onSelect={laadSporters} />
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* ── STAP 2: SPORTERS ── */}
      {stap === "sporters" && (
        <>
          {sportersLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-40 rounded-2xl bg-white/3 animate-pulse" />
              ))}
            </div>
          ) : gefiltereSporters.length === 0 ? (
            <div className="text-center py-16">
              <Users size={40} className="text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400 font-semibold">
                {zoekterm
                  ? `Geen sporters gevonden voor "${zoekterm}"`
                  : "Geen sporters beschikbaar"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="admin-train-sporters-grid">
              {gefiltereSporters.map(s => (
                <SporterTegel
                  key={s.id}
                  sporter={s}
                  onSelect={startSessie}
                  onProfiel={setProfielSporter}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Loading overlay bij starten sessie */}
      {starting && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-12 h-12 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-4"
              style={{ borderColor: GOLD, borderTopColor: "transparent" }} />
            <p className="text-white font-bold">Sessie starten...</p>
          </div>
        </div>
      )}

      {/* Sporter Profiel Modal */}
      {profielSporter && (
        <SporterProfielModal
          sporter={profielSporter}
          token={token}
          onClose={() => setProfielSporter(null)}
          onStartTraining={startSessie}
        />
      )}
    </div>
  );
}
